import { gql } from "@apollo/client";
import { CircularProgress, Dialog, IconButton, Paper } from "@mui/material";
import VideoThumbnail from "controls/VideoThumbnail";
import b64ToBlob from "helpers/b64ToBlob";
import { dialogContext } from "helpers/contexts";
import { useActionFragment } from "hooks/useAction";
import useData from "hooks/useData";
import { progressBar } from "hooks/useProgressBar";
import useShowMessage from "hooks/useShowMessage";
import { remove } from "lodash-es";
import { AlertCircle } from "mdi-material-ui";
import React, { useEffect, useRef, useState } from "react";

import SphereCameraPreview from "./SphereCameraPreview";

// Each preview holds a Rails thread while its Lambda calls back into Rails, so limit them on long lists
const MAX_CONCURRENT_PREVIEWS = 5;
let activePreviews = 0;
const queuedPreviews = [];

function runQueuedPreviews() {
  while (activePreviews < MAX_CONCURRENT_PREVIEWS && queuedPreviews.length) {
    // Previews currently on screen go first, then the rest in page order
    const preview = queuedPreviews.find((p) => p.onScreen) || queuedPreviews[0];
    remove(queuedPreviews, (p) => p === preview);
    activePreviews++;
    preview.run().finally(() => {
      activePreviews--;
      runQueuedPreviews();
    });
  }
}

function queuePreview(run) {
  const preview = { run, onScreen: false };
  queuedPreviews.push(preview);
  runQueuedPreviews();
  return preview;
}

// Removes the preview from the queue if it hasn't started yet
function dequeuePreview(preview) {
  remove(queuedPreviews, (p) => p === preview);
}

export default function SurfaceThumbnail({ id, typename, ...others }) {
  const showMessage = useShowMessage();
  const ref = useRef();
  const [dialogOpen, dialogOpenSet] = useState(false);
  const [previewImageUrl, previewImageUrlSet] = useState(null);
  // Starts as loading so queued thumbnails show a spinner
  const [previewLoading, previewLoadingSet] = useState(true);
  const [previewErrorLogs, previewErrorLogsSet] = useState(null);
  const surfacePreviewMotionCrop = useActionFragment("surfacePreviewMotionCrop", "jpgImageBase64 previewErrorLogs");
  const [data] = useData(
    gql`
      query SurfaceThumbnail($id: ID!) {
        surface(id: $id) {
          id
          projectionShape
          fovHorizontalDegrees
          fovVerticalDegrees
          pitchDegrees
          rollDegrees
          sphereCameraFovHorizontalDegrees
          sphereCameraPitchDegrees
        }
      }
    `,
    { id },
  );

  useEffect(() => {
    let cancelled = false;
    previewLoadingSet(true);

    const preview = queuePreview(() =>
      progressBar(async () => {
        let result;
        try {
          // Suppress the error dialog, which would otherwise open for every failed thumbnail in a list
          result = await surfacePreviewMotionCrop({ input: { surfaceId: id } }, { suppressError: true });
        } catch (error) {
          if (cancelled) return;
          previewLoadingSet(false);
          previewErrorLogsSet(error.message);
          return;
        }
        if (cancelled) return;
        const { jpgImageBase64, previewErrorLogs } = result.surfacePreviewMotionCrop;
        previewLoadingSet(false);
        if (!jpgImageBase64) {
          previewErrorLogsSet(previewErrorLogs);
          return;
        }
        const blob = b64ToBlob(jpgImageBase64, "image/jpeg");
        const url = URL.createObjectURL(blob);
        previewImageUrlSet(url);
      }),
    );

    // Track whether the thumbnail is on (or near) the screen, so it can jump ahead in the queue
    let observer;
    if (ref.current && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          preview.onScreen = entries[entries.length - 1].isIntersecting;
        },
        { rootMargin: "200px" },
      );
      observer.observe(ref.current);
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      dequeuePreview(preview);
    };
  }, [id]);

  return (
    <Paper
      {...others}
      ref={ref}
      style={{ display: "flex", justifyContent: "center", alignItems: "center", ...others.style }}
    >
      {previewLoading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          {previewImageUrl ? (
            <>
              {data?.surface.projectionShape === "sphere" ? (
                <SphereCameraPreview
                  disabled
                  motioncropPreviewUrl={previewImageUrl}
                  fovHorizontalDegrees={data.surface.fovHorizontalDegrees}
                  fovVerticalDegrees={data.surface.fovVerticalDegrees}
                  pitchDegrees={data.surface.pitchDegrees}
                  rollDegrees={data.surface.rollDegrees}
                  sphereCameraFovHorizontalDegrees={data.surface.sphereCameraFovHorizontalDegrees}
                  sphereCameraPitchDegrees={data.surface.sphereCameraPitchDegrees}
                  style={{ width: "100%", height: "100%", cursor: "pointer" }}
                  onClick={() => dialogOpenSet(true)}
                />
              ) : (
                <VideoThumbnail thumbnailUrl={previewImageUrl} style={{ width: "100%", height: "100%" }} />
              )}
            </>
          ) : (
            <IconButton
              size="small"
              onClick={() =>
                showMessage({
                  type: "error",
                  modal: true,
                  header: "Error loading VC preview",
                  content: (
                    <>
                      Error logs:
                      <pre>
                        <code>{previewErrorLogs}</code>
                      </pre>
                    </>
                  ),
                })
              }
            >
              <AlertCircle color="error" />
            </IconButton>
          )}
        </>
      )}
      <dialogContext.Provider value={{ isInDialog: true }}>
        <Dialog open={dialogOpen} onClose={() => dialogOpenSet(false)} fullWidth maxWidth="lg">
          {data && (
            <SphereCameraPreview
              motioncropPreviewUrl={previewImageUrl}
              fovHorizontalDegrees={data.surface.fovHorizontalDegrees}
              fovVerticalDegrees={data.surface.fovVerticalDegrees}
              pitchDegrees={data.surface.pitchDegrees}
              rollDegrees={data.surface.rollDegrees}
              sphereCameraFovHorizontalDegrees={data.surface.sphereCameraFovHorizontalDegrees}
              sphereCameraPitchDegrees={data.surface.sphereCameraPitchDegrees}
              style={{ width: "100%", cursor: "pointer" }}
            />
          )}
        </Dialog>
      </dialogContext.Provider>
    </Paper>
  );
}
